import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Storefront, ShoppingCart, CaretLeft, Minus, Plus } from "@phosphor-icons/react";
import { addToCart, cartCount } from "@/lib/shopCart";

const rp = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");

export default function PublicShopProduct() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [variantId, setVariantId] = useState("");
  const [qty, setQty] = useState(1);
  const [count, setCount] = useState(cartCount());

  useEffect(() => {
    document.title = "Produk — HalloGBS";
    api.get(`/public/shop/products/${id}`).then((r) => {
      setP(r.data);
      const firstAvail = (r.data.variants || []).find((v) => v.stock > 0) || r.data.variants[0];
      setVariantId(firstAvail?.variant_id || "");
    }).catch((e) => { toast.error(apiError(e)); nav("/belanja"); });
    const sync = () => setCount(cartCount());
    window.addEventListener("shop-cart-changed", sync);
    return () => window.removeEventListener("shop-cart-changed", sync);
  }, [id, nav]);

  if (!p) return <div className="flex min-h-screen items-center justify-center bg-[#FAF7FF] text-slate-400">Memuat…</div>;

  const variant = p.variants.find((v) => v.variant_id === variantId) || p.variants[0];
  const stock = variant?.stock || 0;
  const canAdd = stock > 0 && qty > 0 && qty <= stock;

  const add = () => {
    if (!canAdd) return;
    addToCart({ product_id: p.id, variant_id: variant.variant_id, name: p.name,
      variant_label: variant.label, price: (variant.discount_active ? variant.price_final : variant.price), img_id: p.images[0]?.img_id || null, stock, qty });
    toast.success("Ditambahkan ke keranjang");
  };

  return (
    <div className="min-h-screen bg-[#FAF7FF]" data-testid="public-shop-product">
      <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-pink-100 bg-white/90 px-4 py-3 backdrop-blur">
        <Link to="/belanja" data-testid="product-back" className="flex items-center gap-1.5 text-sm font-bold text-[#7C4DE0]"><CaretLeft size={18} weight="bold" /> Belanja</Link>
        <Link to="/belanja/keranjang" data-testid="product-cart-link" className="relative flex items-center gap-1.5 rounded-full bg-[#7C4DE0] px-3 py-1.5 text-xs font-bold text-white">
          <ShoppingCart size={16} weight="fill" /> Keranjang
          {count > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#DB2777] px-1 text-[10px] font-extrabold text-white">{count}</span>}
        </Link>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-5">
        <div className="overflow-hidden rounded-3xl border border-pink-100 bg-white">
          <div className="aspect-square w-full overflow-hidden bg-[#F3ECFF]">
            {p.images[imgIdx] ? (
              <img src={`${API}/public/shop/products/${p.id}/image/${p.images[imgIdx].img_id}`} alt={p.name} className="h-full w-full object-cover" data-testid="product-main-image" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-[#C9B6F0]"><Storefront size={64} weight="duotone" /></div>
            )}
          </div>
          {p.images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto p-3">
              {p.images.map((im, i) => (
                <button key={im.img_id} onClick={() => setImgIdx(i)} data-testid={`product-thumb-${i}`} className={`h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 ${i === imgIdx ? "border-[#DB2777]" : "border-transparent"}`}>
                  <img src={`${API}/public/shop/products/${p.id}/image/${im.img_id}`} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 rounded-3xl border border-pink-100 bg-white p-4">
          <h1 className="font-heading text-xl font-extrabold text-[#1E1B4B]" data-testid="product-name">{p.name}</h1>
          <div className="mt-1 flex items-center gap-2" data-testid="product-price">
            {variant?.discount_active ? (
              <>
                <span className="text-lg font-extrabold text-[#DB2777]">{rp(variant.price_final)}</span>
                <span className="text-sm text-slate-400 line-through">{rp(variant.price)}</span>
                <span data-testid="product-discount-badge" className="rounded-full bg-[#FCE7F3] px-1.5 py-0.5 text-[10px] font-extrabold text-[#DB2777]">-{variant.percent_off}%</span>
              </>
            ) : (
              <span className="text-lg font-extrabold text-[#DB2777]">{rp(variant?.price)}</span>
            )}
          </div>
          {p.description && <p className="mt-2 whitespace-pre-line text-sm text-slate-600">{p.description}</p>}

          <div className="mt-4">
            <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">Pilih Varian</p>
            <div className="flex flex-wrap gap-2" data-testid="product-variants">
              {p.variants.map((v) => (
                <button key={v.variant_id} data-testid={`product-variant-${v.variant_id}`} disabled={v.stock <= 0}
                  onClick={() => { setVariantId(v.variant_id); setQty(1); }}
                  className={`rounded-xl border px-3 py-2 text-xs font-bold transition-colors ${v.variant_id === variantId ? "border-[#7C4DE0] bg-[#F5F0FF] text-[#6A38C7]" : "border-pink-100 bg-white text-slate-600"} ${v.stock <= 0 ? "cursor-not-allowed opacity-40" : ""}`}>
                  {v.label}
                  <span className="ml-1 font-normal text-slate-400">· {v.stock > 0 ? `stok ${v.stock}` : "habis"}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Jumlah</p>
            <div className="flex items-center rounded-xl border border-pink-100">
              <button data-testid="product-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 text-slate-500"><Minus size={14} weight="bold" /></button>
              <span data-testid="product-qty" className="min-w-8 text-center text-sm font-bold">{qty}</span>
              <button data-testid="product-qty-plus" onClick={() => setQty((q) => Math.min(stock, q + 1))} className="px-3 py-2 text-slate-500"><Plus size={14} weight="bold" /></button>
            </div>
            <span data-testid="product-stock" className="text-xs text-slate-400">Stok: {stock}</span>
          </div>

          <button data-testid="product-add-to-cart" onClick={add} disabled={!canAdd}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#7C4DE0] py-3.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#6A38C7] active:scale-[0.99] disabled:opacity-50">
            <ShoppingCart size={18} weight="fill" /> {stock > 0 ? "Tambah ke Keranjang" : "Stok Habis"}
          </button>
        </div>
      </div>
    </div>
  );
}
